import React, { useState, useEffect } from 'react';

import Api from 'utils/api';

import styles from './ProductTypeTab.module.css';

const ProductTypeTab = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // 모달 및 폼 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // 수정 후
  const [formData, setFormData] = useState({
    productTypeId: null,
    productTypeNameKo: '',
    productTypeNameEn: '',
    productTypeNameJa: '',
    productTypeNameZh: '',
    category: 'COIN'
  });

  const CATEGORY_OPTIONS = [
    { value: 'COIN', label: '코인' },
    { value: 'PHYSICAL', label: '실물 상품' },
  ];

  const getCategoryLabel = (category) => {
    return CATEGORY_OPTIONS.find(o => o.value === category)?.label || '-';
  };

  useEffect(() => {
    fetchTypeList();
  }, []);

  // 1. 리스트 조회 (GET)
  const fetchTypeList = async () => {
    setLoading(true);
    const data = await Api.get('/product/type');
    setTypes(data.productTypeList || []);
    setLoading(false);
  };

  const handleOpenModal = (mode, data = null) => {
    setIsEditMode(mode === 'edit');
    setFormData(data ? { ...data } : { productTypeId: null, productTypeNameKo: '', productTypeNameEn: '', productTypeNameJa: '', productTypeNameZh: '', category: 'COIN' });
    setIsModalOpen(true);
  };

  // 2. 저장 로직 (POST)
  const handleSave = async () => {
    // 간단한 유효성 검사 (한국어는 필수라고 가정)
    if (!formData.productTypeNameKo.trim()) {
      return alert("한국어 분류 명칭은 필수입니다.");
    }

    try {
      let response;
      const requestBody = {
        productTypeNameKo: formData.productTypeNameKo,
        productTypeNameEn: formData.productTypeNameEn,
        productTypeNameJa: formData.productTypeNameJa,
        productTypeNameZh: formData.productTypeNameZh,
        category: formData.category,
      };

      if (isEditMode) {
        // 수정 (PATCH) - productTypeId 포함
        response = await Api.patch('/product/type', {
          ...requestBody,
          productTypeId: formData.productTypeId
        });
      } else {
        // 등록 (POST)
        response = await Api.post('/product/type', requestBody);
      }

      if (!response) return;
      alert(isEditMode ? "수정이 완료되었습니다." : "등록이 완료되었습니다.");
      setIsModalOpen(false);
      fetchTypeList();
    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버 오류가 발생했습니다.");
    }
  };

  // 3. 삭제 로직 (DELETE)
  const handleDelete = async (id) => {
    if (!window.confirm("이 분류를 삭제하시겠습니까?")) return;

    const data = await Api.delete(`/product/type?productTypeId=${id}`)

    if (data.code === 200) {
      alert("삭제가 완료되었습니다.");
      fetchTypeList(); // 삭제 후 목록 최신화
    } else {
      alert(`삭제 실패: ${data.message || '오류가 발생했습니다.'}`);
    }

  };

  if (loading) return <div className={styles.centerMsg}>분류 정보를 불러오는 중...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.countInfo}>등록된 분류 <span className={styles.countNumber}>{types.length}</span></div>
        <button className={styles.addBtn} onClick={() => handleOpenModal('add')}>+ 분류 등록</button>
      </div>

      <div className={styles.cardGrid}>
        {types.map((f) => (
          <div key={f.productTypeId} className={styles.productTypeCard}>
            <div className={styles.cardHeader}>
              <div>
                <h3 className={styles.typeName}>{f.productTypeNameKo}</h3>
                <span className={`${styles.categoryBadge} ${f.category === 'COIN' ? styles.badgeCoin : styles.badgePhysical}`}>
                  {getCategoryLabel(f.category)}
                </span>
              </div>
            </div>

            <div className={styles.cardFooter}>
              <button className={styles.editBtn} onClick={() => handleOpenModal('edit', f)}>수정</button>
              <button className={styles.deleteBtn} onClick={() => handleDelete(f.productTypeId)}>삭제</button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{isEditMode ? '분류 수정' : '새 분류 등록'}</h3>
              <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formContainer}>
                {/* 카테고리 */}
                <div className={styles.inputGroup}>
                  <label>카테고리</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className={styles.modalInput}
                  >
                    {CATEGORY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {/* 한국어 */}
                <div className={styles.inputGroup}>
                  <label>분류 명칭 (한국어)</label>
                  <input
                    value={formData.productTypeNameKo}
                    onChange={(e) => setFormData({ ...formData, productTypeNameKo: e.target.value })}
                    className={styles.modalInput}
                  />
                </div>
                {/* 영어 */}
                <div className={styles.inputGroup}>
                  <label>분류 명칭 (영어)</label>
                  <input
                    value={formData.productTypeNameEn}
                    onChange={(e) => setFormData({ ...formData, productTypeNameEn: e.target.value })}
                    className={styles.modalInput}
                  />
                </div>
                {/* 일본어 */}
                <div className={styles.inputGroup}>
                  <label>분류 명칭 (일본어)</label>
                  <input
                    value={formData.productTypeNameJa}
                    onChange={(e) => setFormData({ ...formData, productTypeNameJa: e.target.value })}
                    className={styles.modalInput}
                  />
                </div>
                {/* 중국어 */}
                <div className={styles.inputGroup}>
                  <label>분류 명칭 (중국어)</label>
                  <input
                    value={formData.productTypeNameZh}
                    onChange={(e) => setFormData({ ...formData, productTypeNameZh: e.target.value })}
                    className={styles.modalInput}
                  />
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setIsModalOpen(false)}>취소</button>
              <button className={styles.saveBtn} onClick={handleSave}>저장하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTypeTab;